import dayjs from 'dayjs';
import { UserRole } from '../apolloClient.types';
import type { CurrentUserFragment } from '../contexts/UserSession/Usersession.api';

export const minPasswordLength = 14;

// temporary disable eslint, need to be remove after add more utils
export const isDateInPeriod = (
    selectedDate: dayjs.Dayjs,
    start: Date | string,
    end: Date | string,
    durationType: 'day' | 'month'
): boolean => {
    return (
        (dayjs(start).isBefore(selectedDate, durationType) && dayjs(end).isAfter(selectedDate, durationType)) ||
        dayjs(start).isSame(selectedDate, durationType) ||
        dayjs(end).isSame(selectedDate, durationType)
    );
};

const __imageDataUrlCache = new Map<string, string>();

export const toDataUrl = async (src: string): Promise<string> => {
    if (__imageDataUrlCache.has(src)) {
        return __imageDataUrlCache.get(src)!;
    }
    const res = await fetch(src);
    if (!res.ok) {
        throw new Error(`toDataUrl: failed to fetch ${src} (${res.status})`);
    }
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
    __imageDataUrlCache.set(src, dataUrl);
    return dataUrl;
};

export const isTrainerUser = (user: CurrentUserFragment): boolean => {
    return user.__typename === 'TenantUser' && user.role === UserRole.Trainer;
};

export const renderAvatarName = (firstName: string, lastName: string | null | undefined): string => {
    const first = firstName?.[0] ?? '';
    const last = lastName?.[0] ?? '';
    return `${first}${last}`.toUpperCase();
};

/**
 * Splits text into lines while keeping conjunction words with the preceding word.
 * E.g., "Calm and Patience" -> ["Calm and", "Patience"]
 * E.g., "Focus & Presence" -> ["Focus &", "Presence"]
 * @param text - The text to split
 * @param conjunctionWords - Array of conjunction words to keep with preceding word (default: ['&', 'and'])
 */
export const splitTextWithConjunctions = (text: string, conjunctionWords: string[] = ['&', 'and']): string[] => {
    const lines: string[] = [];
    const words = text.split(' ');

    let i = 0;
    while (i < words.length) {
        const word = words[i];
        const nextWord = words[i + 1];

        if (nextWord && conjunctionWords.includes(nextWord)) {
            // Combine current word with conjunction word
            lines.push(`${word} ${nextWord}`);
            i += 2; // Skip the next word (conjunction)
        } else {
            lines.push(word);
            i += 1;
        }
    }

    return lines;
};

/**
 * Formats file size in bytes to human-readable string (B, KB, MB, GB)
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
        return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Formats duration in seconds to MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g., "02:30")
 */
export const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};
